/**
 * Navbar selected city — one JSON cookie: id, names, and coordinates from `GET /cities`.
 */

import { DEFAULT_WORKSTATION_COORDS } from "@/lib/workstations";

/** Single cookie holding the full selected-city object. */
export const NAV_CITY_COOKIE = "petromin_selected_city";

/** @deprecated Separate keys — cleared on read/write; kept for migration only. */
export const NAV_CITY_ID_COOKIE = "petromin_city_id";
/** @deprecated */
export const NAV_CITY_NAME_COOKIE = "petromin_city_name";
/** @deprecated */
export const NAV_CITY_EN_NAME_COOKIE = "petromin_city_name_en";
/** @deprecated */
export const NAV_CITY_LATITUDE_COOKIE = "petromin_city_latitude";
/** @deprecated */
export const NAV_CITY_LONGITUDE_COOKIE = "petromin_city_longitude";

const LEGACY_SPLIT_COOKIE_KEYS = [
  NAV_CITY_ID_COOKIE,
  NAV_CITY_NAME_COOKIE,
  NAV_CITY_EN_NAME_COOKIE,
  NAV_CITY_LATITUDE_COOKIE,
  NAV_CITY_LONGITUDE_COOKIE,
] as const;

/** Fired on `window` after nav city storage is updated. */
export const NAV_CITY_CHANGED_EVENT = "petromin-nav-city-changed";

/** Legacy `localStorage` key — migrated into {@link NAV_CITY_COOKIE} on read. */
export const LEGACY_NAV_CITY_STORAGE_KEY = "petromin-selected-city";

export type NavCityCoords = {
  latitude: string;
  longitude: string;
};

export type SelectedNavCity = {
  id: number | string;
  name: string;
  nameEn: string;
  latitude?: string;
  longitude?: string;
};

/** Default Riyadh center — matches `DEFAULT_WORKSTATION_COORDS`. */
export const DEFAULT_NAV_CITY_COORDS: NavCityCoords = {
  latitude: DEFAULT_WORKSTATION_COORDS.latitude,
  longitude: DEFAULT_WORKSTATION_COORDS.longitude,
};

const DEFAULT_NAV_CITY_NAME_AR = "الرياض";
const DEFAULT_NAV_CITY_NAME_EN = "Riyadh";

/** Display-only fallback when cookie is not set yet (see {@link ensureNavCityFromApi}). */
export function getDefaultNavCityDisplayNames(): {
  name: string;
  nameEn: string;
} {
  return { name: DEFAULT_NAV_CITY_NAME_AR, nameEn: DEFAULT_NAV_CITY_NAME_EN };
}

/** Legacy GoGo numeric ids — Petromin CS `GET /cities` uses UUIDs. */
export function isLegacyGogoCityId(id: number | string): boolean {
  if (typeof id === "number") return Number.isFinite(id);
  const s = id.trim();
  if (!s) return true;
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
  ) {
    return false;
  }
  return /^\d+$/.test(s);
}

/** ~1 year */
const MAX_AGE_SEC = 60 * 60 * 24 * 365;

function dispatchNavCityChanged(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(NAV_CITY_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

function cookieAttrs(): string {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? ";Secure"
      : "";
  return `path=/;max-age=${MAX_AGE_SEC};SameSite=Lax${secure}`;
}

function cookieClearAttrs(): string {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? ";Secure"
      : "";
  return `path=/;max-age=0;SameSite=Lax${secure}`;
}

function readCookieMap(): Map<string, string> {
  const m = new Map<string, string>();
  if (typeof document === "undefined") return m;
  for (const part of document.cookie.split(";")) {
    const idx = part.indexOf("=");
    if (idx < 0) continue;
    const k = part.slice(0, idx).trim();
    const v = part.slice(idx + 1).trim();
    if (k) {
      try {
        m.set(k, decodeURIComponent(v));
      } catch {
        m.set(k, v);
      }
    }
  }
  return m;
}

export function isValidNavCityCoordPair(lat: string, lng: string): boolean {
  const la = parseFloat(lat);
  const lo = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
  return la >= -90 && la <= 90 && lo >= -180 && lo <= 180;
}

function normalizeCoords(
  latitude?: string,
  longitude?: string
): NavCityCoords | undefined {
  const lat = latitude?.trim() ?? "";
  const lng = longitude?.trim() ?? "";
  if (!lat || !lng || !isValidNavCityCoordPair(lat, lng)) return undefined;
  return { latitude: lat, longitude: lng };
}

function normalizeSelectedNavCity(raw: unknown): SelectedNavCity | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const idRaw = o.id;
  const id =
    typeof idRaw === "number" && Number.isFinite(idRaw)
      ? idRaw
      : typeof idRaw === "string" && idRaw.trim() !== ""
        ? idRaw.trim()
        : null;
  const name = typeof o.name === "string" ? o.name.trim() : "";
  if (id == null || id === "" || !name) return null;
  const nameEnRaw = typeof o.nameEn === "string" ? o.nameEn.trim() : "";
  const nameEn = nameEnRaw || name;
  const coords = normalizeCoords(
    typeof o.latitude === "string" ? o.latitude : undefined,
    typeof o.longitude === "string" ? o.longitude : undefined
  );
  return { id, name, nameEn, ...(coords ?? {}) };
}

function parseCityJson(raw: string | undefined | null): SelectedNavCity | null {
  if (!raw?.trim()) return null;
  try {
    return normalizeSelectedNavCity(JSON.parse(raw));
  } catch {
    return null;
  }
}

function cityToJson(city: SelectedNavCity): string {
  const payload: Record<string, string | number> = {
    id: city.id,
    name: city.name,
    nameEn: city.nameEn,
  };
  const coords = normalizeCoords(city.latitude, city.longitude);
  if (coords) {
    payload.latitude = coords.latitude;
    payload.longitude = coords.longitude;
  }
  return JSON.stringify(payload);
}

function clearLegacyCityStorageClient(): void {
  if (typeof document === "undefined") return;
  const clearAttrs = cookieClearAttrs();
  for (const key of LEGACY_SPLIT_COOKIE_KEYS) {
    document.cookie = `${key}=;${clearAttrs}`;
  }
  try {
    localStorage.removeItem(LEGACY_NAV_CITY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function migrateFromSplitCookies(m: Map<string, string>): SelectedNavCity | null {
  const idRaw = m.get(NAV_CITY_ID_COOKIE);
  const nameRaw = m.get(NAV_CITY_NAME_COOKIE);
  const nameEnRaw = m.get(NAV_CITY_EN_NAME_COOKIE);
  if (idRaw == null || nameRaw == null || nameRaw === "") return null;
  const idNum = Number(idRaw);
  const id = Number.isFinite(idNum) ? idNum : idRaw.trim();
  if (id === "" || id == null) return null;
  const nameEn =
    nameEnRaw != null && nameEnRaw !== "" ? nameEnRaw : nameRaw;
  const lat = m.get(NAV_CITY_LATITUDE_COOKIE)?.trim();
  const lng = m.get(NAV_CITY_LONGITUDE_COOKIE)?.trim();
  const coords = normalizeCoords(lat, lng);
  return { id, name: nameRaw, nameEn, ...(coords ?? {}) };
}

function migrateFromLocalStorage(): SelectedNavCity | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(LEGACY_NAV_CITY_STORAGE_KEY);
    return parseCityJson(raw);
  } catch {
    return null;
  }
}

export function writeSelectedNavCity(city: SelectedNavCity): void {
  if (typeof document === "undefined") return;
  const attrs = cookieAttrs();
  document.cookie = `${NAV_CITY_COOKIE}=${encodeURIComponent(cityToJson(city))};${attrs}`;
  clearLegacyCityStorageClient();
  dispatchNavCityChanged();
}

export function writeSelectedCityCookies(
  id: number | string,
  name: string,
  nameEn: string,
  coords?: NavCityCoords | null
): void {
  writeSelectedNavCity({
    id,
    name,
    nameEn,
    ...(coords?.latitude && coords?.longitude
      ? { latitude: coords.latitude, longitude: coords.longitude }
      : {}),
  });
}

export function readSelectedCityFromCookies(): SelectedNavCity | null {
  const m = readCookieMap();
  const fromObject = parseCityJson(m.get(NAV_CITY_COOKIE));
  if (fromObject) {
    if (isLegacyGogoCityId(fromObject.id)) {
      return null;
    }
    const hasLegacySplit = LEGACY_SPLIT_COOKIE_KEYS.some((k) => m.has(k));
    let hasLegacyStorage = false;
    try {
      hasLegacyStorage =
        typeof window !== "undefined" &&
        localStorage.getItem(LEGACY_NAV_CITY_STORAGE_KEY) != null;
    } catch {
      /* ignore */
    }
    if (hasLegacySplit || hasLegacyStorage) clearLegacyCityStorageClient();
    return fromObject;
  }

  const fromSplit = migrateFromSplitCookies(m);
  if (fromSplit && !isLegacyGogoCityId(fromSplit.id)) {
    writeSelectedNavCity(fromSplit);
    return fromSplit;
  }

  return null;
}

/** Client-only: coordinates from the selected-city object cookie. */
export function readNavCityCoordsFromCookies(): NavCityCoords | null {
  const city = readSelectedCityFromCookies();
  return normalizeCoords(city?.latitude, city?.longitude) ?? null;
}

/** Server Components / Route Handlers — read coords from the selected-city cookie. */
export function readNavCityCoordsFromRequestCookies(cookies: {
  get: (name: string) => { value: string } | undefined;
}): NavCityCoords | null {
  const raw = cookies.get(NAV_CITY_COOKIE)?.value;
  if (raw) {
    try {
      const decoded = decodeURIComponent(raw);
      const city = parseCityJson(decoded);
      const coords = normalizeCoords(city?.latitude, city?.longitude);
      if (coords) return coords;
    } catch {
      /* fall through to legacy split cookies */
    }
  }

  const m = new Map<string, string>();
  for (const key of LEGACY_SPLIT_COOKIE_KEYS) {
    const v = cookies.get(key)?.value?.trim();
    if (v) m.set(key, v);
  }
  const fromSplit = migrateFromSplitCookies(m);
  return normalizeCoords(fromSplit?.latitude, fromSplit?.longitude) ?? null;
}

/**
 * Client-only: reads cookie / migrates legacy storage. Does **not** write a default
 * city with legacy GoGo id `1` — use {@link ensureNavCityFromApi} for that.
 */
export function resolveSelectedNavCityClient(): SelectedNavCity | null {
  if (typeof window === "undefined") return null;
  const fromCookies = readSelectedCityFromCookies();
  if (fromCookies) return fromCookies;

  const fromStorage = migrateFromLocalStorage();
  if (fromStorage) {
    if (isLegacyGogoCityId(fromStorage.id)) {
      return null;
    }
    writeSelectedNavCity(fromStorage);
    return fromStorage;
  }

  return null;
}
